import { FC } from "react";
import { CompanySettingsViewProps, hasCompaniesInThisCustomRole } from "./companySettingsDomain";
import { observer } from "mobx-react";
import { useTranslation } from "@/stores/RootStoreContext";
import { Box, VBox } from "@/components-v2/Box";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { MSelect } from "@/components-v2/Select";
import { DropdownMenu, DropdownMenuItemProps } from "@/components-v2/Dropdown";
import { Table } from "@/components-v2/Table";
import { Tag } from "@/components-v2/Tag";
import { LastLogin } from "@/common/commonComponents";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { confirmAction, thunk } from "@/events/eventsDomain";
import { OptionFieldModel } from "@/common/commonForms";
import { PortfolioCompany } from "@/stores/PortfolioStore";
import { UserItem } from "@/common/common";
import { compact } from "lodash";
import { Markdown } from "@/text/Markdown.tsx";

export const UsersMainPanel: FC<CompanySettingsViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <VBox container marginTop={"lg"} padding={"lg"} maxWidth={"xl"}>
      <h2>{t("users")}</h2>
      {props.isLoading && <LoadingIndicator />}
      {!props.isLoading && <UsersTab {...props} />}
    </VBox>
  );
});

const UsersTab: FC<CompanySettingsViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <VBox gap="lg">
      <Box justifyContent={"space-between"}>
        <Box gap={"md"}>
          <MSelect model={props.usersByRoleModel} />
          {props.usersByRoleModel.value.type !== "all_roles" && (
            <LinkButton
              label={t("showAll")}
              icon={"arrow-counter-clockwise"}
              onClick={props.showAll}
            />
          )}
        </Box>
        <Box gap={"lg"}>
          {props.canEdit && !props.canInviteAdmin && (
            <Box alignItems={"center"}>
              <Markdown value={t("settings.users.cannot-invite-admin")} />
            </Box>
          )}
          <Box alignItems={"center"}>
            <Markdown value={t("settings.permissions.link")} enableLinks />
          </Box>
          {props.canEdit && (
            <DropdownMenu
              button={{
                type: "button",
                variant: "secondary",
                label: t("invite-new"),
              }}
              items={compact<DropdownMenuItemProps>([
                props.canInviteAdmin && {
                  type: "item",
                  label: t("accessType.ADMIN"),
                  onSelect: () => props.openSendInvitationDialog(),
                },
                ...props.customRoles.map<DropdownMenuItemProps>((c) => ({
                  type: "item",
                  label: c.role.customRoleName,
                  onSelect: () => props.openSendInvitationDialog(c.role),
                })),
              ])}
            />
          )}
        </Box>
      </Box>
      <Table>
        <thead>
          <tr>
            <th align={"left"}>{t("name")}</th>
            <th align={"left"}>{t("settings.role")}</th>
            <th align={"left"}>{t("email")}</th>
            <th align={"left"}>{t("last-login")}</th>
          </tr>
        </thead>
        <tbody>
          {props.users.map(({ key, name, email, tagLabel, lastLogin, onRevoke }) => (
            <tr key={key}>
              <td align={"left"}>{name}</td>
              <td align={"left"}>
                <Tag label={tagLabel.label} color={"info"} />
              </td>
              <td align={"left"}>{email}</td>
              <td align={"left"}>{<LastLogin lastLogin={lastLogin} />}</td>
              <td>
                {props.canEdit && onRevoke && (
                  <Box gap={"md"} justifyContent={"end"}>
                    <IconButton
                      icon={"trash"}
                      color={"error"}
                      tooltip={t("settings.revokeAdminAccess")}
                      onClick={confirmAction({
                        text: {
                          key: "settings.revokeAdminAccessForQuestion",
                          params: { name },
                        },
                        onConfirm: [thunk(onRevoke)],
                      })}
                      compact
                    />
                  </Box>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </VBox>
  );
});

export function CompaniesRoleSelected({
  selectedOrgNos,
  filteredCompanies,
  usersUsingRole,
  orgNo,
}: {
  selectedOrgNos: string[];
  filteredCompanies: PortfolioCompany[];
  usersUsingRole: UserItem[];
  orgNo: string;
}): OptionFieldModel<PortfolioCompany> {
  return new OptionFieldModel<PortfolioCompany>({
    options: filteredCompanies.filter((company) => company.organisationNumber !== orgNo),
    selectedIds: compact([orgNo, ...usersUsingRole.map((user) => user.orgNo), ...selectedOrgNos]),
    idFn: (item) => item.organisationNumber,
    labelFn: (item) => {
      const flag = hasCompaniesInThisCustomRole(item, usersUsingRole, orgNo);
      if (flag) {
        return (
          <Box alignItems={"center"}>
            <div>{item.name}</div>
            <Tag label={{ key: "roles.sharing.assigned-to-users" }} color={"warning"} />
          </Box>
        );
      } else {
        return { translated: item.name };
      }
    },
    disabledFn: (item) => hasCompaniesInThisCustomRole(item, usersUsingRole, orgNo),
  });
}
